import type { PermissionContext } from '@ardoq/access-control';
import type { ComponentPreviewState } from '@ardoq/component-overview/miniOverview';
import type { I18nLocale } from '@ardoq/i18n';
import { RepresentationData } from '@ardoq/data-model';
import { IconName } from '@ardoq/icons';
import { NavigatorComponents, NavigatorEventListeners } from '../types';
import { NodeModelTypes } from '../utils/consts';
import {
  AriaAttributes,
  CSSProperties,
  HTMLAttributes,
  RefCallback,
} from 'react';
import { LoaderColor } from '@ardoq/loader';
import type {
  LoadErrorMessageKey,
  LoadingMessageKey,
} from '../streams/loadStatusKey';

export type ComponentItemProps = {
  type: NodeModelTypes;
  name: string;
  showContextMenuButton: boolean;
  containerStyle: {
    paddingLeft: string;
  };
  containerClassNames: string;
  componentRepresentationData: RepresentationData | null;
  iconWrapperClassNames: string;
  iconWrapperStyle: {
    fill: string;
    color: string;
  };
  isExternallyManaged: boolean;
  isExternallyMissing: boolean;
  isLocked: boolean;
  isDiffTypeChanged: boolean;
  hasWriteAccess: boolean;
  linkIconName: IconName;
  isInDiffMode: boolean;
  aggregatedVisualDiffState: number;
  nodeId: string;
  hasVisibleContent: boolean;
  isExpanded: boolean;
  /** The row the keyboard highlight currently sits on, moved with the arrow
   * keys while the search input keeps DOM focus. */
  isHighlighted: boolean;
  showStartCreateReferenceIcon: boolean;
  hasIncomingRef: boolean;
  hasOutgoingRef: boolean;
  isSelected: boolean;
  isSelectable: boolean;
  isDisabledOption: boolean;
  modifierKey: string;
  actionKey: string;
  isAllSelected: boolean;
  ariaAttributes: AriaAttributes;
  contextMenuAriaLabel: string;
  /** True when any row holds the keyboard highlight, not just this one. */
  hasKeyboardHighlight: boolean;
  /** True for every row while a drag is in progress, not just the dragged one. */
  isDragging: boolean;
  /** Null unless the pane runs with hover previews and one has loaded. */
  componentPreview: ComponentPreviewState | null;
  permissionContext: PermissionContext | null;
  i18nLocale: I18nLocale;
};

/**
 * Anchor wiring for a details popover: ref and pointer handlers only. ARIA
 * belongs on the row's `treeitem`, which the row links to the open card
 * through `detailsId`.
 */
export type ComponentItemTriggerProps = Pick<
  HTMLAttributes<HTMLDivElement>,
  'onMouseEnter' | 'onMouseLeave'
> & {
  ref?: RefCallback<HTMLElement>;
};

export type HoverPreviewRowProps = ComponentItemProps & {
  triggerProps?: ComponentItemTriggerProps;
  /**
   * Id of the currently open details popover describing this row. Linked from
   * the `treeitem` with `aria-details` — not `aria-expanded`, which the
   * treeitem already uses for its subtree, nor `aria-haspopup`, which would
   * promise a keyboard interaction the tree does not offer.
   */
  detailsId?: string;
};

export const isComponentItemProps = (
  candidate: RenderProps
): candidate is ComponentItemProps =>
  candidate.type === NodeModelTypes.COMPONENT;

export type WorkspaceItemProps = {
  // common?
  type: NodeModelTypes;
  name: string;
  nodeId: string;
  containerClassNames: string;
  containerStyle: {
    paddingLeft: string;
  };
  hasVisibleContent: boolean;
  isExpanded: boolean;
  isExternallyManaged: boolean;
  // common?
  hasCloseButton: boolean;
  hasContextMenu: boolean;
  ariaAttributes: AriaAttributes;
};

export const isWorkspaceItemProps = (
  candidate: RenderProps
): candidate is WorkspaceItemProps =>
  candidate.type === NodeModelTypes.WORKSPACE;

export type ScenarioItemProps = {
  type: NodeModelTypes;
  name: string;
  nodeId: string;
  containerClassNames: string;
  containerStyle: {
    paddingLeft: string;
  };
  hasVisibleContent: boolean;
  isExpanded: boolean;
  scenarioIconName: IconName;
  ariaAttributes: AriaAttributes;
};

export const isScenarioItemProps = (
  candidate: RenderProps
): candidate is ScenarioItemProps => candidate.type === NodeModelTypes.SCENARIO;

export type CreateComponentItemProps = {
  type: NodeModelTypes;
  name: string;
  label: string;
  nodeId: string;
  containerClassNames: string;
  containerStyle: {
    paddingLeft: string;
  };
  ariaAttributes: AriaAttributes;
};

export const isCreateComponentItemProps = (
  candidate: RenderProps
): candidate is CreateComponentItemProps =>
  candidate.type === NodeModelTypes.CREATE_COMPONENT;

export type RenderProps =
  | ComponentItemProps
  | CreateComponentItemProps
  | Record<string, any>;

export type DragTargetProps = {
  shouldShowGhost: boolean;
  ghostStyle: CSSProperties;
  ghostText: string;
  onTransitionEnd?: () => void;
  targetId: string;
};

export type NavigatorContentProps = {
  renderProps: RenderProps[];
  navigatorContentStyle: CSSProperties;
  parentDropTargetIndicatorStyle: CSSProperties | null;
  layoutBoxesContainerStyle: CSSProperties;
  actionNamespace?: string;
  name: string;
  isMultiselectable?: boolean;
  components: NavigatorComponents;
  navigatorContentId?: string;
  navigatorContentClassName?: string;
};

export type LoadDataNotificationProps = {
  loadingScopeDataNotification: string | null;
  loaderColor?: LoaderColor;
};

export type LoadDataErrorProps = {
  errorMessage: string;
};

export type EmptyFilterResultProps = {
  message: string;
};

export type NavigatorContainerProps = NavigatorContentProps & {
  eventListeners: NavigatorEventListeners;
  dragElementRoot?: HTMLElement;
  navigatorContainerClassName: string;
  dragGhostProps: DragTargetProps;
  isInitialized: boolean;
  isFilterResultEmpty: boolean;
  dataLoadingMessage: LoadingMessageKey | null;
  dataErrorMessage: LoadErrorMessageKey | null;
  backgroundColor?: string;
  components: NavigatorComponents;
  isControlledContainerHeight: boolean;
  navigatorContainerId?: string;
  navigatorContentId?: string;
  navigatorContentClassName?: string;
  onRootResize?: () => void;
};
